import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy2026 = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: January 14, 2026
          </p>
        </div>
      </section>

      <section className="pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="prose prose-lg max-w-none">
              <div className="mb-12">
                <p className="text-gray-700 leading-relaxed mb-4">
                  This Privacy Policy describes how Asha Health, Inc. ("Asha," "we," "us," or "our") collects, uses,
                  discloses, and protects personal information when you use our websites, applications, and services that
                  link to this Privacy Policy, including asha.health, asha.dental, and their subdomains (collectively, the
                  "Services").
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Asha provides practice management, billing, revenue cycle management, communications, and related
                  technology services to dental practices. When a dental practice uses our Services, we may process
                  information about patients, practice staff, and other individuals on behalf of that dental practice. In
                  many cases, Asha acts as a service provider, processor, or HIPAA business associate to the dental
                  practice, and our use of that information is limited by our agreements with the practice and
                  applicable law.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This Privacy Policy does not replace your dental provider's Notice of Privacy Practices. If your request
                  concerns protected health information maintained by your dental provider, your provider's notice and
                  procedures may also apply.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-3xl font-bold mb-6">Summary</h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>We do not sell personal information.</li>
                  <li>We do not share personal information with third parties for their own marketing or promotional purposes.</li>
                  <li>We do not share personal information for cross-context behavioral advertising or targeted advertising.</li>
                  <li>We receive information from you, from your dental provider, from insurers, payers, and clearinghouses, and from our operation of the Services.</li>
                  <li>We use personal information to provide, secure, support, and improve the Services; communicate with you; support dental care, billing, and claims workflows; and comply with legal obligations.</li>
                  <li>We disclose information only as needed to operate the Services, support your dental provider, process payments or claims, comply with law, protect rights and safety, or as otherwise directed or authorized.</li>
                  <li>We process protected health information ("PHI") in accordance with HIPAA, applicable business associate agreements, and our customer agreements.</li>
                </ul>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">1. Information We Collect</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The personal information we collect depends on how you interact with us, how your dental provider uses
                  the Services, and which Services are enabled.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may collect the following categories of information:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Identifiers and contact information, such as name, email address, phone number, mailing address, billing address, account identifiers, and similar information.</li>
                  <li>Account and authentication information, such as login credentials, user roles, authentication logs, and account settings.</li>
                  <li>Appointment and scheduling information, such as appointment requests, confirmations, reminders, cancellations, provider assignments, and related communications.</li>
                  <li>Communications and support information, such as messages, calls, chat transcripts, support requests, feedback, and other communications with us or through the Services.</li>
                  <li>Billing, payment, insurance, and claims information, such as payment status, invoices, insurance information, payer communications, claim details, and related revenue cycle information. Payment card, bank, or ACH information may be processed by our payment providers.</li>
                  <li>Dental and health-related information, such as information related to dental care, treatment, diagnoses, procedures, prescriptions, clinical notes, referrals, eligibility, benefits, and claims, when processed on behalf of your dental provider.</li>
                  <li>Device, usage, and technical information, such as IP address, browser type, device identifiers, operating system, pages viewed, timestamps, log data, and similar information collected through cookies, analytics tools, and security technologies.</li>
                  <li>Business contact and customer account information, such as names, roles, contact details, billing information, and communications for dental practice personnel, vendors, and business contacts.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">We collect information from:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>You directly, when you use the Services, contact us, complete forms, communicate with your provider through the Services, or otherwise provide information to us.</li>
                  <li>Your dental provider, when the provider uses the Services to manage patient care, scheduling, billing, communications, claims, or practice operations.</li>
                  <li>Insurance companies, payers, clearinghouses, and other entities involved in eligibility, billing, payment, and claims processing.</li>
                  <li>Service providers and subprocessors that help us operate the Services.</li>
                  <li>Automatic collection technologies, such as cookies, logs, and similar tools used to operate, secure, measure, and improve the Services.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  We do not purchase personal information from data brokers or marketing partners.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">2. How We Use Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use personal information for the following purposes, depending on the Services used:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>To provide, operate, maintain, and support the Services.</li>
                  <li>To enable scheduling, appointment reminders, patient communications, billing, payment processing, insurance eligibility, claims processing, revenue cycle management, and practice management workflows.</li>
                  <li>To create, administer, authenticate, and secure accounts.</li>
                  <li>To communicate with patients, practice staff, customers, vendors, and business contacts.</li>
                  <li>To respond to support requests, questions, and feedback.</li>
                  <li>To send administrative messages, service updates, security notices, billing notices, and other non-marketing communications.</li>
                  <li>To monitor, secure, debug, and protect the Services, including detecting fraud, abuse, security incidents, and unauthorized access.</li>
                  <li>To improve the Services, develop new features, and understand how the Services are used, using de-identified, aggregated, or otherwise appropriately protected information where required.</li>
                  <li>To comply with legal, regulatory, contractual, accounting, tax, and compliance obligations.</li>
                  <li>To enforce agreements, protect rights and safety, and defend legal claims.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  When we process information on behalf of a dental practice, we use that information only as permitted by
                  our agreements with the practice, applicable law, and the instructions or configuration provided through
                  the Services.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">3. Artificial Intelligence and Automation</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Asha uses software automation and artificial intelligence, including AI-powered agents and workflow
                  tools, to help deliver and improve the Services. These tools may assist with scheduling, communications,
                  billing support, practice operations, and related workflows.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not use automated processing to make legal, eligibility, clinical, or similarly significant
                  decisions about individuals without appropriate safeguards. AI and automation features operate subject to
                  contractual, technical, and legal controls.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We do not permit third-party AI service providers to use PHI, patient data, or dental-practice customer
                  data to train their general-purpose models. We also do not use PHI, patient data, or dental-practice
                  customer data to train third-party foundation models. We may use de-identified or aggregated information
                  to improve the Services where permitted by applicable law and our agreements.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">4. When We Disclose Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We disclose personal information only as needed to operate the Services and for the purposes described in
                  this Privacy Policy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">We may disclose information to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li><strong>Dental providers and care teams.</strong> We disclose information to your dental provider and others involved in your dental care or practice operations so they can provide services to you and use the Services.</li>
                  <li><strong>Service providers and subprocessors.</strong> We use vendors that help us provide hosting, infrastructure, security, communications, analytics, support, payment processing, and other operational services. These providers are bound by contractual confidentiality and data protection obligations and may use information only to provide services to us or on our behalf. Where required, we enter into HIPAA business associate agreements with vendors that create, receive, maintain, or transmit PHI on our behalf.</li>
                  <li><strong>Insurance companies, payers, clearinghouses, and payment processors.</strong> We disclose information as needed for eligibility checks, billing, payments, claims processing, remittance, collections, and related workflows.</li>
                  <li><strong>Authorized recipients.</strong> We may disclose information to individuals or entities you or your dental provider direct or authorize us to contact.</li>
                  <li><strong>Legal and regulatory authorities.</strong> We may disclose information when required by law, subpoena, court order, regulatory request, or similar process, or when we believe disclosure is necessary to protect rights, safety, security, or the integrity of the Services.</li>
                  <li><strong>Business transaction parties.</strong> If we are involved in a merger, acquisition, financing, reorganization, sale of assets, bankruptcy, or similar transaction, information may be disclosed as part of that transaction, subject to appropriate protections.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  We do not sell personal information. We do not disclose personal information to third parties for their
                  own marketing or promotional purposes. We do not share personal information for cross-context behavioral
                  advertising or targeted advertising.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">5. Cookies and Tracking Technologies</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our websites and online services may use cookies, logs, and similar technologies to operate and secure
                  the Services, remember preferences, authenticate users, measure performance, understand usage, and improve
                  the Services.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">These technologies may include:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Strictly necessary technologies required for site functionality, authentication, security, and fraud prevention.</li>
                  <li>Preference technologies that remember choices and settings.</li>
                  <li>Analytics technologies that help us understand performance and usage.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not use advertising pixels or third-party tracking technologies on patient-facing or authenticated
                  health-information pages for targeted advertising. We do not disclose PHI to advertising or tracking
                  vendors for targeted advertising.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Where required by law, we obtain consent before setting non-essential cookies and provide a way to manage
                  preferences. You can also control cookies through your browser settings, although some Services may not
                  function properly if cookies are disabled.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">6. How Long We Keep Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We retain personal information for as long as reasonably necessary to provide the Services, fulfill the
                  purposes described in this Privacy Policy, comply with legal and contractual obligations, resolve
                  disputes, enforce agreements, maintain security, and support legitimate business needs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our retention periods depend on the type of information and the context in which we process it:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Account, customer, and business contact information is generally retained for the duration of the relationship and for a reasonable period afterward as needed for legal, accounting, security, and business purposes.</li>
                  <li>Billing, payment, tax, accounting, and contract records are retained as required or permitted by law and our legitimate business needs.</li>
                  <li>Security logs, technical logs, and audit records are retained for periods appropriate to security, compliance, troubleshooting, and fraud-prevention purposes.</li>
                  <li>PHI and dental records are retained, returned, or deleted in accordance with applicable law, our business associate agreements, our customer agreements, and the instructions of the dental provider, where feasible.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  When we no longer need information, we delete, de-identify, aggregate, or otherwise handle it in
                  accordance with applicable law and our agreements.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">7. How We Protect Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use administrative, physical, and technical safeguards designed to protect personal information,
                  including PHI, from unauthorized access, use, disclosure, alteration, and destruction.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These safeguards may include encryption, access controls, authentication, logging, monitoring, workforce
                  training, vendor review, security policies, incident response processes, and other controls appropriate to
                  the nature of the information we process.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  No method of transmission over the Internet or electronic storage is completely secure. We cannot
                  guarantee absolute security, but we work to protect information using safeguards appropriate to our
                  Services and legal obligations.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">8. HIPAA and Protected Health Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When Asha creates, receives, maintains, or transmits PHI on behalf of a dental practice, Asha acts as a
                  HIPAA business associate. We use and disclose PHI only as permitted by HIPAA, applicable business
                  associate agreements, our customer agreements, and applicable law.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We enter into business associate agreements with dental practices where required and, where applicable,
                  with vendors that handle PHI on our behalf. We maintain safeguards designed to protect the privacy and
                  security of PHI.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Your dental provider's Notice of Privacy Practices also governs how your PHI is used and disclosed. If
                  you want to access, amend, restrict, or request an accounting of disclosures of PHI maintained by your
                  dental provider, you may need to contact that provider directly. We may help your provider respond to such
                  requests where required by law or our agreement with the provider.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">9. Minors</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Services may be used by dental practices that treat patients of all ages, including minors. When we
                  process a minor's information on behalf of a dental provider, we do so as directed by the provider and in
                  accordance with applicable law.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Our Services are not directed to children under 13 for independent use without appropriate consent. We do
                  not knowingly collect personal information directly from children in a manner that requires verifiable
                  parental consent under applicable law without obtaining such consent. If you believe we collected
                  information from a child without appropriate authorization, please contact us at info@asha.health.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">10. Your Privacy Rights</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Depending on where you live and the type of information involved, you may have rights to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>Request access to personal information.</li>
                  <li>Request correction of inaccurate personal information.</li>
                  <li>Request deletion of personal information.</li>
                  <li>Request a portable copy of personal information.</li>
                  <li>Restrict or object to certain processing.</li>
                  <li>Opt out of sale, sharing, targeted advertising, or certain profiling, where applicable.</li>
                  <li>Limit certain uses of sensitive personal information, where applicable.</li>
                  <li>Appeal a decision regarding a privacy request, where applicable.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To make a request, contact us at info@asha.health. We may need to verify your identity before responding.
                  You may also use an authorized agent to submit a request where permitted by law, subject to verification.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Because much of the information we process is handled on behalf of dental practices, some requests may
                  need to be directed to, or coordinated with, your dental provider. We will not discriminate against you
                  for exercising privacy rights.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">11. U.S. State Privacy Rights</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Residents of California and other U.S. states with comprehensive privacy laws may have additional rights,
                  subject to legal exceptions. Information governed by HIPAA, state medical privacy laws, or other
                  health-specific laws may be exempt from some state consumer privacy law requirements.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For personal information subject to these state privacy laws:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>We do not sell personal information.</li>
                  <li>We do not share personal information for cross-context behavioral advertising.</li>
                  <li>We do not process personal information for targeted advertising.</li>
                  <li>We use sensitive personal information only for purposes reasonably necessary to provide the Services, maintain security, comply with law, or as otherwise permitted by applicable law.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  You may exercise applicable state privacy rights by contacting us at info@asha.health. If your request is
                  denied, you may appeal by replying to our response or contacting us again at the same email address with
                  "Privacy Appeal" in the subject line.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">12. Opt-Out Preference Signals and Do-Not-Track</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Some browsers and devices offer opt-out preference signals, such as Global Privacy Control, or
                  Do-Not-Track signals.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Where required by applicable law, we honor recognized opt-out preference signals as requests to opt out of
                  sale or sharing for the browser or device sending the signal. Because we do not sell personal information
                  or share it for cross-context behavioral advertising, these signals generally do not change how we
                  provide the Services.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  There is not a uniform industry standard for responding to Do-Not-Track signals. We respond to legally
                  recognized opt-out preference signals as described above.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">13. International Users</h3>
                <p className="text-gray-700 leading-relaxed">
                  Asha is based in the United States, and the Services are primarily intended for use by dental practices
                  and individuals in the United States. If you access the Services from outside the United States, your
                  information may be processed in the United States, where privacy laws may differ from those in your
                  location.
                </p>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-4">14. Changes to This Privacy Policy</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. The updated version will be identified by a revised
                  "Last updated" date and will be effective when posted unless otherwise stated.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If we make material changes that materially affect your privacy rights or how we use personal information,
                  we will provide additional notice or obtain consent where required by law.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4">15. Contact Us</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions about this Privacy Policy or want to exercise privacy rights, contact us at:
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="font-semibold text-gray-900 mb-2">Asha Health, Inc.</p>
                  <p className="text-gray-700">Email: info@asha.health</p>
                  <p className="text-gray-700">Website: www.asha.health</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy2026;
